/**
 * One plain layout for client emails (payment request, reminder, receipt,
 * file delivery, ticket auto-reply). Pure — no imports — so edge functions
 * send it and the CRM renders the identical HTML as the preview.
 *
 * Email-safe: table layout, inline styles, white page, 550px column, small
 * logo top left, links as underlined text.
 */

export type BillingEmailBrand = {
  name: string;
  logoUrl?: string | null;
  /** Legacy full-width header image; the plain layout shows logoUrl only. */
  bannerUrl?: string | null;
  phone?: string | null;
  email?: string | null;
  website?: string | null;
};

export type BillingEmailHero = {
  /** Heading (e.g. "Your files are ready"); defaults to the amount. */
  title?: string | null;
  /** Amount (e.g. "$412.26"), listed as "<amountLabel>: <amount>". */
  amount?: string | null;
  /** "Amount" by default. */
  amountLabel?: string | null;
  /** Line above the heading ("Due October 8, 2026", "Paid in full"). */
  status?: string | null;
  statusTone?: "neutral" | "success" | "warning";
  /** Small lines (e.g. "Invoice INV-2026-0164"). */
  meta?: string[];
  cta?: { label: string; url: string } | null;
};

export type BillingEmailSection =
  | { kind: "list"; title: string; items: string[] }
  | {
      kind: "files";
      title: string;
      files: Array<{ name: string; url?: string | null }>;
      note?: string | null;
    }
  | { kind: "rows"; title?: string; rows: Array<[string, string]> };

const COLORS = {
  text: "#1F2328",
  soft: "#59636E",
  faint: "#818B98",
  line: "#E1E4E8",
  link: "#0B57D0",
};

const FONT = "Arial,Helvetica,sans-serif";

export const escapeEmailHtml = (value: string) =>
  value
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;");

const P_STYLE = `margin:0 0 16px;font:400 15px/24px ${FONT};color:${COLORS.text};`;

/** Plain text (paragraphs split by blank lines) → email paragraphs. */
export const emailParagraphs = (text: string) =>
  text
    .trim()
    .split(/\n{2,}/)
    .filter(Boolean)
    .map(
      (block) =>
        `<p style="${P_STYLE}">${escapeEmailHtml(block).replace(/\n/g, "<br>")}</p>`,
    )
    .join("");

/** "(203) 303-9148" → "tel:+12033039148". */
const telHref = (phone: string) => {
  const digits = phone.replace(/\D/g, "");
  return `tel:+${digits.length === 10 ? `1${digits}` : digits}`;
};

const link = (label: string, href: string, color = COLORS.link) =>
  `<a href="${escapeEmailHtml(href)}" target="_blank" style="color:${color};text-decoration:underline;">${escapeEmailHtml(label)}</a>`;

const phoneLinkHtml = (phone: string) =>
  `<a href="${telHref(phone)}" style="color:${COLORS.soft};text-decoration:underline;white-space:nowrap;">${escapeEmailHtml(phone)}</a>`;

// Client emails are documents: always light, never theme-adapted. "light only"
// stops Apple Mail / iOS / Outlook from auto-inverting; every element also
// carries inline light styles for clients that drop <style>.
const HEAD_STYLES = `<style>
  :root{color-scheme:light only;supported-color-schemes:light;}
  body{margin:0;padding:0;width:100% !important;-webkit-text-size-adjust:100%;-ms-text-size-adjust:100%;}
  table{border-collapse:collapse;mso-table-lspace:0;mso-table-rspace:0;}
  img{border:0;line-height:100%;outline:none;text-decoration:none;-ms-interpolation-mode:bicubic;}
  .fname{word-break:break-word;overflow-wrap:anywhere;}
  @media only screen and (max-width:600px){
    .container{width:100% !important;}
    .px{padding-left:20px !important;padding-right:20px !important;}
  }
</style>`;

/** Small logo top left (about 24px tall), or the company name. */
const brandHeader = (brand: BillingEmailBrand) =>
  brand.logoUrl
    ? `<img src="${escapeEmailHtml(brand.logoUrl)}" alt="${escapeEmailHtml(brand.name)}" height="24" style="display:block;height:24px;width:auto;max-width:160px;border:0;font:700 15px ${FONT};color:${COLORS.text};">`
    : `<p style="margin:0;font:700 15px/24px ${FONT};color:${COLORS.text};">${escapeEmailHtml(brand.name)}</p>`;

const bullets = (items: string[]) =>
  items.length
    ? `<ul style="margin:0 0 16px;padding:0 0 0 22px;">${items
        .map(
          (item) =>
            `<li style="margin:0 0 4px;font:400 15px/24px ${FONT};color:${COLORS.text};">${item}</li>`,
        )
        .join("")}</ul>`
    : "";

/** Hero facts as bullets: status, amount, meta (e.g. invoice number). */
const heroBullets = (hero: BillingEmailHero) =>
  [
    hero.status,
    hero.amount ? `${hero.amountLabel ?? "Amount"}: ${hero.amount}` : null,
    ...(hero.meta ?? []),
  ]
    .filter((item): item is string => Boolean(item?.trim()))
    .map(escapeEmailHtml);

const sectionTitle = (title: string) =>
  `<p style="margin:0 0 6px;font:700 15px/24px ${FONT};color:${COLORS.text};">${escapeEmailHtml(title)}</p>`;

const sectionHtml = (section: BillingEmailSection) => {
  if (section.kind === "files") {
    if (!section.files.length) return "";
    const items = section.files.map((file) =>
      file.url
        ? `<span class="fname">${link(file.name, file.url)}</span>`
        : `<span class="fname">${escapeEmailHtml(file.name)}</span>`,
    );
    const note = section.note?.trim()
      ? `<p style="${P_STYLE}color:${COLORS.soft};">${escapeEmailHtml(section.note.trim())}</p>`
      : "";
    return `${sectionTitle(section.title)}${bullets(items)}${note}`;
  }
  if (section.kind === "list") {
    if (!section.items.length) return "";
    return `${sectionTitle(section.title)}${bullets(section.items.map(escapeEmailHtml))}`;
  }
  if (!section.rows.length) return "";
  return `${section.title ? sectionTitle(section.title) : ""}${bullets(
    section.rows.map(
      ([label, value]) =>
        `${escapeEmailHtml(label)}: ${escapeEmailHtml(value)}`,
    ),
  )}`;
};

/**
 * Who reads replies. Billing mail comes from an unmonitored sender, so it
 * says so; file deliveries come from the ticket inbox, where replies land.
 */
export type BillingEmailReplies = "unmonitored" | "welcome";

const footerHtml = (
  brand: BillingEmailBrand,
  replies: BillingEmailReplies,
  footerNote?: string | null,
) => {
  const phone = brand.phone?.trim();
  const email = brand.email?.trim();
  const phoneLink = phone ? phoneLinkHtml(phone) : null;
  const emailLink = email ? link(email, `mailto:${email}`, COLORS.soft) : null;
  const contact = [emailLink, phoneLink].filter(Boolean).join(" or ");
  const note =
    replies === "welcome"
      ? `For questions, reply to this email${phoneLink ? ` or call ${phoneLink}` : ""}.`
      : `This is an automated message, please don't reply.${contact ? ` For questions contact ${contact}.` : ""}`;
  const small = `margin:0 0 14px;font:400 13px/20px ${FONT};color:${COLORS.soft};`;
  return `<p style="margin:24px 0 0;font:400 15px/24px ${FONT};color:${COLORS.text};">${escapeEmailHtml(brand.name)} Team</p>
      <hr style="border:0;border-top:1px solid ${COLORS.line};margin:24px 0;">
      <p style="${small}">&copy; ${new Date().getFullYear()} ${escapeEmailHtml(brand.name)}</p>
      ${footerNote?.trim() ? `<p style="${small}">${escapeEmailHtml(footerNote.trim())}</p>` : ""}
      <p style="${small}">${note}</p>`;
};

/**
 * Plain document style: small logo, greeting, message, facts as bullets,
 * links as underlined text, sections, team sign-off and a short footer.
 */
export const renderBillingEmail = (params: {
  brand: BillingEmailBrand;
  /** Inbox preview line (hidden in the body). */
  preheader?: string | null;
  /** "Hi Ana," line above the message. */
  greeting?: string | null;
  hero?: BillingEmailHero | null;
  /** Message as plain text (blank lines = paragraphs). */
  message?: string | null;
  sections?: BillingEmailSection[];
  /** "unmonitored" (default) asks the client not to reply. */
  replies?: BillingEmailReplies;
  /** Extra footer line (e.g. "También hablamos español"). */
  footerNote?: string | null;
}) => {
  const { brand } = params;
  const facts = params.hero ? bullets(heroBullets(params.hero)) : "";
  const cta = params.hero?.cta
    ? `<p style="${P_STYLE}">${link(params.hero.cta.label, params.hero.cta.url)}</p>`
    : "";
  const sections = (params.sections ?? []).map(sectionHtml).join("");

  return `<!doctype html>
<html lang="en" xmlns="http://www.w3.org/1999/xhtml" xmlns:v="urn:schemas-microsoft-com:vml" xmlns:o="urn:schemas-microsoft-com:office:office"><head><meta charset="utf-8"><meta name="viewport" content="width=device-width,initial-scale=1"><meta http-equiv="X-UA-Compatible" content="IE=edge"><meta name="x-apple-disable-message-reformatting"><meta name="format-detection" content="telephone=no, address=no, email=no"><meta name="color-scheme" content="light only"><meta name="supported-color-schemes" content="light"><title>${escapeEmailHtml(brand.name)}</title>
<!--[if mso]><noscript><xml><o:OfficeDocumentSettings><o:PixelsPerInch>96</o:PixelsPerInch></o:OfficeDocumentSettings></xml></noscript><style>td,th,div,p,a,li{font-family:Arial,Helvetica,sans-serif !important;}</style><![endif]-->
${HEAD_STYLES}</head>
<body style="margin:0;padding:0;background:#ffffff;">
${
  params.preheader
    ? `<div style="display:none;max-height:0;overflow:hidden;mso-hide:all;font-size:1px;line-height:1px;">${escapeEmailHtml(params.preheader)}&#847;&#8203;&#847;&#8203;&#847;&#8203;&#847;&#8203;&#847;&#8203;&#847;&#8203;</div>`
    : ""
}
<table role="presentation" width="100%" cellpadding="0" cellspacing="0" border="0" bgcolor="#ffffff" style="background:#ffffff;">
  <tr><td align="center" style="padding:40px 12px;">
    <!--[if mso]><table role="presentation" width="550" cellpadding="0" cellspacing="0" border="0"><tr><td><![endif]-->
    <table role="presentation" class="container" width="550" cellpadding="0" cellspacing="0" border="0" style="width:550px;max-width:550px;">
      <tr><td class="px" align="left" style="padding:0 0 40px;">${brandHeader(brand)}</td></tr>
      <tr><td class="px" align="left" style="font-family:${FONT};">
        ${params.greeting?.trim() ? `<p style="${P_STYLE}">${escapeEmailHtml(params.greeting.trim())}</p>` : ""}
        ${params.hero?.title?.trim() ? `<p style="${P_STYLE}font-weight:700;">${escapeEmailHtml(params.hero.title.trim())}</p>` : ""}
        ${params.message?.trim() ? emailParagraphs(params.message) : ""}
        ${facts}
        ${cta}
        ${sections}
        ${footerHtml(brand, params.replies ?? "unmonitored", params.footerNote)}
      </td></tr>
    </table>
    <!--[if mso]></td></tr></table><![endif]-->
  </td></tr>
</table>
</body></html>`;
};

/** Plain-text twin for clients that do not render HTML. */
export const renderBillingEmailText = (params: {
  brandName: string;
  hero?: BillingEmailHero | null;
  message?: string | null;
  sections?: BillingEmailSection[];
  contact?: string[];
  replies?: BillingEmailReplies;
}) =>
  [
    params.message?.trim(),
    params.hero
      ? [
          params.hero.title,
          params.hero.status,
          params.hero.amount
            ? `${params.hero.amountLabel ?? "Amount"}: ${params.hero.amount}`
            : null,
          ...(params.hero.meta ?? []),
        ]
          .filter(Boolean)
          .join("\n") || null
      : null,
    params.hero?.cta
      ? `${params.hero.cta.label}: ${params.hero.cta.url}`
      : null,
    ...(params.sections ?? []).map((section) =>
      section.kind === "list"
        ? section.items.length
          ? `${section.title}\n${section.items.map((item) => `- ${item}`).join("\n")}`
          : null
        : section.kind === "files"
          ? section.files.length
            ? `${section.title}\n${section.files
                .map(
                  (file) => `- ${file.name}${file.url ? `: ${file.url}` : ""}`,
                )
                .join("\n")}${section.note ? `\n${section.note}` : ""}`
            : null
          : section.rows
              .map(([label, value]) => `${label}: ${value}`)
              .join("\n"),
    ),
    (params.replies ?? "unmonitored") === "unmonitored"
      ? "This is an automated message — please don't reply to this email."
      : null,
    [params.brandName, ...(params.contact ?? [])].filter(Boolean).join("\n"),
  ]
    .filter(Boolean)
    .join("\n\n");

/** "2026-10-08" → "October 8, 2026" (date-only, no timezone shift). */
export const formatEmailDate = (value?: string | null) => {
  const raw = value?.trim();
  if (!raw) return null;
  const date = new Date(
    /^\d{4}-\d{2}-\d{2}$/.test(raw) ? `${raw}T12:00:00Z` : raw,
  );
  if (Number.isNaN(date.getTime())) return null;
  return date.toLocaleDateString("en-US", {
    month: "long",
    day: "numeric",
    year: "numeric",
    timeZone: "UTC",
  });
};
